import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import {
  ArrowLeft, Mic, Plus, Pencil, Trash2, Save, X, Loader2, Copy, Check, ChevronDown,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL } from "../lib/constants";
import { toast } from "sonner";

const COLOR_OPTIONS = ["#f68b1f", "#0091be", "#7ac143", "#ffd400", "#083050"];

export default function Pitches() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState("");
  const [copied, setCopied] = useState("");
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    return api.get("/pitches")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(""), 2200);
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/pitches/${p.id}`);
      toast.success("Pitch removed.");
      await reload();
    } catch (err) {
      toast.error("Couldn't delete", { description: err?.response?.data?.detail || err?.message });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link to="/" data-testid="pitches-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <div className="mt-4 flex items-start gap-4 flex-wrap">
        <img src={CJ_AVATAR_URL} alt="CJ" className="h-16 w-16 rounded-full object-cover shadow-md ring-2 ring-white shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <div className="tc-eyebrow text-green-700">Pitch Examples</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">Plays you can run.</h1>
          <p className="text-slate-500 mt-1.5 text-sm max-w-xl">Curated pitches from the team. Copy, adapt, and send. Tap a card to expand.</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setEditing("new")}
            data-testid="pitches-add"
            className="h-11 px-4 rounded-2xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: "#7ac143" }}
          >
            <Plus size={16} /> Add pitch
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-10 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center text-slate-500">
          <Mic size={32} className="mx-auto opacity-40" />
          <div className="mt-3 font-bold">No pitches yet.</div>
          {canEdit && <p className="text-sm mt-1">Click "Add pitch" to create your first.</p>}
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="pitches-list">
          {items.map((p, idx) => (
            <PitchCard
              key={p.id}
              p={p}
              open={openKey === p.id}
              onToggle={() => setOpenKey((cur) => (cur === p.id ? "" : p.id))}
              copied={copied}
              onCopy={copy}
              canEdit={canEdit}
              onEdit={() => setEditing(p)}
              onDelete={() => remove(p)}
              delay={idx * 0.04}
            />
          ))}
        </div>
      )}

      {editing && (
        <PitchEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
}

function PitchCard({ p, open, onToggle, copied, onCopy, canEdit, onEdit, onDelete, delay }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
      className="bg-white rounded-2xl border-2 overflow-hidden"
      style={{ borderColor: open ? p.color : "#e2e8f0" }}
      data-testid={`pitch-card-${p.id}`}
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: p.color }} />
      <div className="flex items-center gap-1 pr-3">
        <button
          onClick={onToggle}
          data-testid={`pitch-toggle-${p.id}`}
          aria-expanded={open}
          className="flex-1 text-left p-4 sm:p-5 flex items-center gap-4 hover:bg-slate-50/60 transition-colors"
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${p.color}1f`, color: p.color }}
          >
            <Mic size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-tc-ink leading-tight">{p.title}</div>
            {p.use_case && <div className="text-xs sm:text-sm text-slate-500 mt-0.5 line-clamp-2">{p.use_case}</div>}
          </div>
          <ChevronDown
            size={20}
            className="text-slate-400 shrink-0 transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {canEdit && (
          <div className="flex items-center gap-0.5">
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`pitch-edit-${p.id}`} className="w-9 h-9 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-tc-ink" title="Edit">
              <Pencil size={14} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`pitch-delete-${p.id}`} className="w-9 h-9 rounded-lg hover:bg-red-50 inline-flex items-center justify-center text-slate-400 hover:text-red-500" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-100"
          >
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color: p.color }}>
                  Copy and adapt
                </div>
                <button
                  onClick={() => onCopy(p.body, `${p.id}-body`)}
                  data-testid={`pitch-copy-${p.id}`}
                  className="h-9 px-3 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors"
                  style={{ backgroundColor: `${p.color}26`, color: p.color }}
                >
                  {copied === `${p.id}-body` ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy pitch</>}
                </button>
              </div>
              <div
                className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 text-[15px] text-slate-700 leading-relaxed whitespace-pre-wrap font-sans"
                data-testid={`pitch-body-${p.id}`}
              >
                {p.body}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

function PitchEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [draft, setDraft] = useState(() => ({
    title: initial?.title || "",
    use_case: initial?.use_case || "",
    body: initial?.body || "",
    color: initial?.color || "#7ac143",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Title is required.");
    if (!draft.body.trim()) return toast.error("Pitch body is required.");
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        use_case: draft.use_case.trim() || null,
        body: draft.body.trim(),
        color: draft.color,
      };
      if (isNew) {
        await api.post("/pitches", payload);
        toast.success("Pitch added.");
      } else {
        await api.put(`/pitches/${initial.id}`, payload);
        toast.success("Pitch updated.");
      }
      await onSaved();
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  const INPUT = "w-full h-11 px-3 rounded-xl bg-white border-2 border-slate-200 focus:border-green-500 outline-none text-sm";

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="pitch-editor-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 180, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        data-testid="pitch-editor"
      >
        <div className="h-2 w-full" style={{ backgroundColor: draft.color }} />
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl text-tc-ink">{isNew ? "Add pitch" : "Edit pitch"}</h2>
            <button onClick={onClose} data-testid="pitch-editor-close" className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
              <X size={18} />
            </button>
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <Label>Title</Label>
              <input value={draft.title} onChange={(e) => set("title", e.target.value)} data-testid="pitch-editor-title" className={INPUT} placeholder="e.g. The 45-year value pitch" />
            </div>
            <div>
              <Label>When to use it (optional)</Label>
              <input value={draft.use_case} onChange={(e) => set("use_case", e.target.value)} data-testid="pitch-editor-use-case" className={INPUT} placeholder="Discovery call, follow-up, board intro..." />
            </div>
            <div>
              <Label>Pitch</Label>
              <textarea
                value={draft.body}
                onChange={(e) => set("body", e.target.value)}
                data-testid="pitch-editor-body"
                rows={10}
                className="w-full px-3 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-green-500 outline-none text-sm font-sans leading-relaxed"
                placeholder="Write the pitch the way you'd say it on the phone..."
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set("color", c)}
                    data-testid={`pitch-editor-color-${c.slice(1)}`}
                    className="w-9 h-9 rounded-lg border-2 transition-transform hover:scale-105"
                    style={{ backgroundColor: c, borderColor: draft.color === c ? "#083050" : "transparent" }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button onClick={onClose} className="h-11 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="pitch-editor-save"
              className="h-11 px-5 rounded-xl text-white font-bold text-sm flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60"
              style={{ background: draft.color }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isNew ? "Add pitch" : "Save changes"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Label({ children }) {
  return <div className="tc-eyebrow text-slate-500 mb-1.5">{children}</div>;
}
